import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

const defaultDdi = "55";

const sanitizeDigits = (value) => (value || "").replace(/\D/g, "");
const sanitizeDdiInput = (value) => {
  const raw = (value || "").replace(/[^\d+]/g, "");
  const digits = raw.replace(/\D/g, "").slice(0, 3);
  const hasPlus = raw.startsWith("+");
  return `${hasPlus ? "+" : ""}${digits}`;
};
const sanitizePhoneInput = (value, maxLength = 15) =>
  (value || "").replace(/\D/g, "").slice(0, maxLength);
const buildPhoneValue = (ddiDigits, phoneDigits, fallbackDdi = defaultDdi) =>
  phoneDigits ? `+${ddiDigits || fallbackDdi}${phoneDigits}` : "";

const getDdiPlusError = (ddiValue) => {
  if (!ddiValue) return "";
  if (!ddiValue.startsWith("+")) {
    return "Inclua o + antes do DDI";
  }
  return "";
};

const getPhoneLengthError = (ddiDigits, numberDigits) => {
  if (!numberDigits) return "";

  const isBrazil = ddiDigits === "55";
  const hasDdi = Boolean(ddiDigits);

  if (isBrazil) {
    if (numberDigits.length !== 10 && numberDigits.length !== 11) {
      return "Para DDI +55, use 10 ou 11 digitos (total 12 ou 13)";
    }
    return "";
  }

  if (!hasDdi) {
    if (numberDigits.length !== 10 && numberDigits.length !== 11) {
      return "Sem DDI, use 10 ou 11 digitos";
    }
    return "";
  }

  const totalDigits = ddiDigits.length + numberDigits.length;
  if (totalDigits < 8 || totalDigits > 15) {
    return "Para DDI diferente de +55, use entre 8 e 15 digitos no total";
  }
  return "";
};

const getPhoneMaxDigits = (ddiDigits) => {
  const hasDdi = Boolean(ddiDigits);
  const isBrazil = ddiDigits === "55";

  if (isBrazil || !hasDdi) {
    return 11;
  }

  const maxByTotal = 15 - ddiDigits.length;
  return Math.max(1, Math.min(15, maxByTotal));
};

const isSamePhone = (originalDigits, newDigits, fallbackDdi) => {
  const fallbackDigits = sanitizeDigits(fallbackDdi);

  if (!originalDigits && !newDigits) return true;
  if (originalDigits === newDigits) return true;
  if (
    fallbackDigits &&
    originalDigits &&
    `${fallbackDigits}${originalDigits}` === newDigits
  ) {
    return true;
  }
  if (
    fallbackDigits &&
    newDigits &&
    `${fallbackDigits}${newDigits}` === originalDigits
  ) {
    return true;
  }
  return false;
};

const ModalEdicaoCorretor = ({
  open,
  corretor,
  usuarioNome = "",
  saving = false,
  success = false,
  errorMessage = "",
  onClose,
  onConfirm,
}) => {
  const telefoneOriginal = useMemo(() => corretor?.telefone || "", [corretor]);
  const celularOriginal = useMemo(() => corretor?.celular || "", [corretor]);

  const [novoNome, setNovoNome] = useState("");
  const [corretorSnapshot, setCorretorSnapshot] = useState(null);
  const [novoTelefoneDdi, setNovoTelefoneDdi] = useState("");
  const [novoTelefoneNumero, setNovoTelefoneNumero] = useState("");
  const [novoCelularDdi, setNovoCelularDdi] = useState("");
  const [novoCelularNumero, setNovoCelularNumero] = useState("");
  const [validationErrors, setValidationErrors] = useState({});


  const splitPhoneParts = (value, fallbackDdi) => {
    const digits = sanitizeDigits(value);
    const fallbackDigits = sanitizeDigits(fallbackDdi);

    if (!digits) {
      return { ddi: fallbackDigits, number: "" };
    }

    // Se o número tem pelo menos 2 dígitos, considera os 2 primeiros como DDI
    if (digits.length >= 2) {
      return {
        ddi: digits.slice(0, 2),
        number: digits.slice(2),
      };
    }

    // Se tem apenas 1 dígito, retorna o fallback como DDI
    return { ddi: fallbackDigits, number: digits };
  };


  useEffect(() => {
    if (open && corretor) {
      setCorretorSnapshot(corretor);
      const telefoneParts = splitPhoneParts(telefoneOriginal, defaultDdi);
      const celularParts = splitPhoneParts(celularOriginal, defaultDdi);
      setNovoNome(corretor.nome || "");
      setNovoTelefoneDdi(`+${telefoneParts.ddi || defaultDdi}`);
      setNovoTelefoneNumero(telefoneParts.number || "");
      setNovoCelularDdi(`+${celularParts.ddi || defaultDdi}`);
      setNovoCelularNumero(celularParts.number || "");
      setValidationErrors({});
    }
  }, [corretor, open, telefoneOriginal, celularOriginal]);

  const nomeAlterado =
    corretor && (novoNome || "").trim() !== (corretor.nome || "");
  const telefoneOriginalDigits = sanitizeDigits(telefoneOriginal);
  const celularOriginalDigits = sanitizeDigits(celularOriginal);
  const novoTelefoneDdiDigits = sanitizeDigits(novoTelefoneDdi);
  const novoTelefoneNumeroDigits = sanitizeDigits(novoTelefoneNumero);
  const novoCelularDdiDigits = sanitizeDigits(novoCelularDdi);
  const novoCelularNumeroDigits = sanitizeDigits(novoCelularNumero);
  const telefoneDdiPlusError = getDdiPlusError(novoTelefoneDdi);
  const celularDdiPlusError = getDdiPlusError(novoCelularDdi);
  const telefoneNumeroMaxDigits = getPhoneMaxDigits(novoTelefoneDdiDigits);
  const celularNumeroMaxDigits = getPhoneMaxDigits(novoCelularDdiDigits);
  const telefoneNovoFinal = buildPhoneValue(
    novoTelefoneDdiDigits,
    novoTelefoneNumeroDigits,
    defaultDdi
  );
  const celularNovoFinal = buildPhoneValue(
    novoCelularDdiDigits,
    novoCelularNumeroDigits,
    defaultDdi
  );
  const novoTelefoneDigits = sanitizeDigits(telefoneNovoFinal);
  const novoCelularDigits = sanitizeDigits(celularNovoFinal);

  const telefoneLengthError = getPhoneLengthError(
    novoTelefoneDdiDigits,
    novoTelefoneNumeroDigits
  );
  const celularLengthError = getPhoneLengthError(
    novoCelularDdiDigits,
    novoCelularNumeroDigits
  );

  const telefoneAlterado =
    Boolean(novoTelefoneDigits) &&
    !isSamePhone(telefoneOriginalDigits, novoTelefoneDigits, defaultDdi);
  const celularAlterado =
    Boolean(novoCelularDigits) &&
    !isSamePhone(celularOriginalDigits, novoCelularDigits, defaultDdi);
  const telefoneLengthOk =
    !telefoneAlterado || (!telefoneLengthError && !telefoneDdiPlusError);
  const celularLengthOk =
    !celularAlterado || (!celularLengthError && !celularDdiPlusError);
  const houveAlteracao = nomeAlterado || telefoneAlterado || celularAlterado;

  const handleConfirm = async () => {
    if (!houveAlteracao) return;

    const errors = {};
    const nomeFinal = (novoNome || "").trim();

    if (!nomeFinal) {
      errors.novoNome = "Informe o nome do corretor";
    }

    if (novoTelefoneNumeroDigits) {
      if (novoTelefoneDdi && !novoTelefoneDdiDigits) {
        errors.novoTelefoneDdi = "Informe o DDI";
      } else if (novoTelefoneDdi && telefoneDdiPlusError) {
        errors.novoTelefoneDdi = telefoneDdiPlusError;
      }

      if (!novoTelefoneNumeroDigits) {
        errors.novoTelefoneNumero = "Informe o numero do telefone";
      } else {
        const lengthError = getPhoneLengthError(
          novoTelefoneDdiDigits,
          novoTelefoneNumeroDigits
        );
        if (lengthError) {
          errors.novoTelefoneNumero = lengthError;
        }
      }
    }

    if (novoCelularNumeroDigits) {
      if (novoCelularDdi && !novoCelularDdiDigits) {
        errors.novoCelularDdi = "Informe o DDI";
      } else if (novoCelularDdi && celularDdiPlusError) {
        errors.novoCelularDdi = celularDdiPlusError;
      }

      if (!novoCelularNumeroDigits) {
        errors.novoCelularNumero = "Informe o numero do celular";
      } else {
        const lengthError = getPhoneLengthError(
          novoCelularDdiDigits,
          novoCelularNumeroDigits
        );
        if (lengthError) {
          errors.novoCelularNumero = lengthError;
        }
      }
    }

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0 || !onConfirm) return;

    const telefoneFinal = telefoneAlterado
      ? (telefoneNovoFinal || telefoneOriginal || "").trim()
      : (telefoneOriginal || "").trim();
    const celularFinal = celularAlterado
      ? (celularNovoFinal || celularOriginal || "").trim()
      : (celularOriginal || "").trim();

    await onConfirm({
      novoNome: nomeFinal,
      novoTelefone: telefoneFinal,
      novoCelular: celularFinal,
      telefoneAtual: telefoneOriginalDigits,
      celularAtual: celularOriginalDigits,
    });
  };

  const corretorHeader = corretorSnapshot || corretor;
  const headerText =
    usuarioNome && corretorHeader?.nome
      ? `Editar dados do corretor ${corretorHeader.nome}`
      : "Editar dados do corretor";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {headerText}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            label="Nome do corretor"
            fullWidth
            value={novoNome}
            onChange={(e) => setNovoNome(e.target.value)}
            error={Boolean(validationErrors.novoNome)}
            helperText={validationErrors.novoNome}
            disabled={saving}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="DDI"
              value={novoTelefoneDdi}
              onChange={(e) => {
                const sanitizedDdi = sanitizeDdiInput(e.target.value);
                const sanitizedDigits = sanitizeDigits(sanitizedDdi);
                setNovoTelefoneDdi(sanitizedDdi);
                setNovoTelefoneNumero((current) =>
                  sanitizePhoneInput(current, getPhoneMaxDigits(sanitizedDigits))
                );
              }}
              error={
                Boolean(validationErrors.novoTelefoneDdi) ||
                Boolean(telefoneDdiPlusError)
              }
              helperText={validationErrors.novoTelefoneDdi || telefoneDdiPlusError}
              disabled={saving}
              inputProps={{ inputMode: "numeric", maxLength: 4 }}
              sx={{ maxWidth: 140 }}
            />
            <TextField
              label="telefone"
              fullWidth
              value={novoTelefoneNumero}
              onChange={(e) =>
                setNovoTelefoneNumero(
                  sanitizePhoneInput(e.target.value, telefoneNumeroMaxDigits)
                )
              }
              error={Boolean(validationErrors.novoTelefoneNumero)}
              helperText={validationErrors.novoTelefoneNumero}
              disabled={saving}
              inputProps={{ inputMode: "numeric", maxLength: telefoneNumeroMaxDigits }}
            />
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="DDI"
              value={novoCelularDdi}
              onChange={(e) => {
                const sanitizedDdi = sanitizeDdiInput(e.target.value);
                const sanitizedDigits = sanitizeDigits(sanitizedDdi);
                setNovoCelularDdi(sanitizedDdi);
                setNovoCelularNumero((current) =>
                  sanitizePhoneInput(current, getPhoneMaxDigits(sanitizedDigits))
                );
              }}
              error={
                Boolean(validationErrors.novoCelularDdi) ||
                Boolean(celularDdiPlusError)
              }
              helperText={validationErrors.novoCelularDdi || celularDdiPlusError}
              disabled={saving}
              inputProps={{ inputMode: "numeric", maxLength: 4 }}
              sx={{ maxWidth: 140 }}
            />
            <TextField
              label="celular"
              fullWidth
              value={novoCelularNumero}
              onChange={(e) =>
                setNovoCelularNumero(
                  sanitizePhoneInput(e.target.value, celularNumeroMaxDigits)
                )
              }
              error={Boolean(validationErrors.novoCelularNumero)}
              helperText={validationErrors.novoCelularNumero}
              disabled={saving}
              inputProps={{ inputMode: "numeric", maxLength: celularNumeroMaxDigits }}
            />
          </Stack>

          {errorMessage && (
            <Alert severity="error" variant="outlined">
              {errorMessage}
            </Alert>
          )}

          {success && (
            <Alert severity="success" variant="outlined">
              Dados do corretor atualizados com sucesso.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Box
          sx={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Button onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {saving && <CircularProgress size={20} thickness={5} />}
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={saving || !houveAlteracao || !telefoneLengthOk || !celularLengthOk}
            >
              Enviar Alterações
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ModalEdicaoCorretor;
